/**
 * Validation utilities for form fields
 * Used for teacher-side student profile editing
 */

export interface ValidationResult {
  isValid: boolean
  error: string | null
}

/**
 * Validate email address using RFC 5322 compliant regex
 * @param email - Email address to validate
 * @returns Validation result with error message if invalid
 */
export const validateEmail = (email: string): ValidationResult => {
  // Allow empty email (optional field)
  if (!email || email.trim() === '') {
    return { isValid: true, error: null }
  }

  const trimmedEmail = email.trim()

  // Strict email validation regex - requires proper domain with TLD
  // Format: localpart@domain.tld
  const strictEmailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

  if (!strictEmailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address (e.g., name@example.com)',
    }
  }

  // Additional checks
  const parts = trimmedEmail.split('@')
  if (parts.length !== 2) {
    return {
      isValid: false,
      error: 'Email must contain exactly one @ symbol',
    }
  }

  const [localPart, domain] = parts

  // Check local part length
  if (localPart.length === 0 || localPart.length > 64) {
    return {
      isValid: false,
      error: 'Email address format is invalid',
    }
  }

  // Check domain has at least one dot
  if (!domain.includes('.')) {
    return {
      isValid: false,
      error: 'Email domain must include a valid extension (e.g., .com, .org)',
    }
  }

  // Check domain parts
  const domainParts = domain.split('.')
  const tld = domainParts[domainParts.length - 1]

  if (tld.length < 2) {
    return {
      isValid: false,
      error: 'Email domain extension must be at least 2 characters',
    }
  }

  return { isValid: true, error: null }
}

/**
 * Validate phone number
 * Supports US format and international E.164 format
 * @param phone - Phone number to validate
 * @returns Validation result with error message if invalid
 */
export const validatePhone = (phone: string): ValidationResult => {
  // Allow empty phone (optional field)
  if (!phone || phone.trim() === '') {
    return { isValid: true, error: null }
  }

  const trimmedPhone = phone.trim()

  // Remove common formatting characters for validation
  const digitsOnly = trimmedPhone.replace(/[\s\-\(\)\+\.]/g, '')

  // Check if contains only digits (after removing formatting)
  if (!/^\d+$/.test(digitsOnly)) {
    return {
      isValid: false,
      error: 'Phone number can only contain digits and formatting characters',
    }
  }

  // International format: starts with +, followed by country code and number
  // E.164 format: +[1-3 digit country code][up to 15 digits]
  if (trimmedPhone.startsWith('+')) {
    // E.164 validation
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return {
        isValid: false,
        error: 'International phone number must be between 10-15 digits',
      }
    }
    return { isValid: true, error: null }
  }

  // US format validation: must have at least 10 digits
  if (digitsOnly.length < 10) {
    return {
      isValid: false,
      error: 'Phone number must be at least 10 digits',
    }
  }

  // US format: exactly 10 or 11 digits (with country code)
  if (digitsOnly.length > 11) {
    return {
      isValid: false,
      error: 'US phone number must be 10 digits (or 11 with country code)',
    }
  }

  // If 11 digits, first digit should be 1 (US country code)
  if (digitsOnly.length === 11 && digitsOnly[0] !== '1') {
    return {
      isValid: false,
      error: 'Invalid country code for US phone number',
    }
  }

  return { isValid: true, error: null }
}

/**
 * Validate all student profile fields
 * Used before saving to ensure data integrity
 */
export interface StudentProfileValidation {
  email: ValidationResult
  phone: ValidationResult
  guardianEmail: ValidationResult
  guardianPhone: ValidationResult
  isValid: boolean
}

export const validateStudentProfile = (data: {
  email?: string
  phone?: string
  guardianEmail?: string
  guardianPhone?: string
}): StudentProfileValidation => {
  const emailValidation = validateEmail(data.email || '')
  const phoneValidation = validatePhone(data.phone || '')
  const guardianEmailValidation = validateEmail(data.guardianEmail || '')
  const guardianPhoneValidation = validatePhone(data.guardianPhone || '')

  return {
    email: emailValidation,
    phone: phoneValidation,
    guardianEmail: guardianEmailValidation,
    guardianPhone: guardianPhoneValidation,
    isValid:
      emailValidation.isValid &&
      phoneValidation.isValid &&
      guardianEmailValidation.isValid &&
      guardianPhoneValidation.isValid,
  }
}
